import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatCurrency as fmtCur } from '../utils/currency';
import { formatDate } from '../utils/date';

const COLLECTION_STATUSES = ['current', 'overdue', 'collected', 'written_off'];
const AGING_BUCKETS = ['0-30', '31-60', '61-90', '90+'];
const PAYMENT_METHODS = ['Wire Transfer', 'Check', 'ACH', 'Credit Card', 'Other'];
const REV_METHODS = ['Percentage of Completion', 'Milestone', 'Straight Line', 'As Invoiced'];

const STATUS_BADGES = {
  current: 'badge-green', overdue: 'badge-red', collected: 'badge-blue', written_off: 'badge-gray'
};

const AGING_COLORS = { '0-30': '#10B981', '31-60': '#F59E0B', '61-90': '#F97316', '90+': '#EF4444' };

export default function Revenue() {
  const { dirty, markDirty, resetDirty } = useFormDirty();
  const [tab, setTab] = useState('revenue');
  const [revRecords, setRevRecords] = useState([]);
  const [revSummary, setRevSummary] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [aging, setAging] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Revenue modal
  const [revModalOpen, setRevModalOpen] = useState(false);
  const [revForm, setRevForm] = useState({ contract_id: '', period: '', recognized: '', deferred: '', unbilled: '', method: '' });

  // Payment modal
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payForm, setPayForm] = useState({ customer_id: '', amount: '', payment_date: '', payment_method: '', bank_reference: '', invoice_id: '', notes: '' });

  // Collection action modal
  const [actionModal, setActionModal] = useState(false);
  const [actionRecId, setActionRecId] = useState(null);
  const [actionForm, setActionForm] = useState({ action_type: '', notes: '', action_date: '' });
  const [actions, setActions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [rrRes, rsRes, rcRes, agRes, pmRes, psRes, ctRes, cuRes, invRes] = await Promise.all([
        apiFetch('/api/revenue'),
        apiFetch('/api/revenue/summary'),
        apiFetch('/api/revenue/receivables'),
        apiFetch('/api/revenue/receivables/aging'),
        apiFetch('/api/revenue/payments'),
        apiFetch('/api/revenue/payments/summary'),
        apiFetch('/api/contracts?limit=500'),
        apiFetch('/api/customers?limit=200'),
        apiFetch('/api/billing/invoices?limit=500')
      ]);
      if (rrRes.ok) { const d = await rrRes.json(); setRevRecords(d.revenueRecognition || []); }
      if (rsRes.ok) { const d = await rsRes.json(); setRevSummary(d.summary || []); }
      if (rcRes.ok) { const d = await rcRes.json(); setReceivables(d.receivables || []); }
      if (agRes.ok) { const d = await agRes.json(); setAging(d.aging || []); }
      if (pmRes.ok) { const d = await pmRes.json(); setPayments(d.payments || []); }
      if (psRes.ok) { const d = await psRes.json(); setPaymentSummary(d.summary || []); }
      if (ctRes.ok) { const d = await ctRes.json(); setContracts(d.contracts || []); }
      if (cuRes.ok) { const d = await cuRes.json(); setCustomers(d.customers || d.data || []); }
      if (invRes.ok) { const d = await invRes.json(); setInvoices(d.invoices || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtCurrency = (v) => fmtCur(v);
  const fmtDate = (v) => formatDate(v, '--');

  // Summary cards
  const totalRecognized = revSummary.reduce((s, r) => s + parseFloat(r.total_recognized || 0), 0);
  const totalDeferred = revSummary.reduce((s, r) => s + parseFloat(r.total_deferred || 0), 0);
  const totalUnbilled = revSummary.reduce((s, r) => s + parseFloat(r.total_unbilled || 0), 0);
  const totalReceivable = receivables.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  async function saveRevenue() {
    const body = {
      contractId: revForm.contract_id || null, period: revForm.period || null,
      recognized: parseFloat(revForm.recognized) || 0, deferred: parseFloat(revForm.deferred) || 0,
      unbilled: parseFloat(revForm.unbilled) || 0, method: revForm.method || null
    };
    try {
      await apiFetch('/api/revenue', { method: 'POST', body: JSON.stringify(body) });
      resetDirty(); setRevModalOpen(false); fetchData();
    } catch (e) { /* handled */ }
  }

  async function savePayment() {
    const allocations = payForm.invoice_id ? [{ invoiceId: payForm.invoice_id }] : [];
    const body = {
      customerId: payForm.customer_id || null, amount: parseFloat(payForm.amount) || 0,
      paymentDate: payForm.payment_date || null, paymentMethod: payForm.payment_method || null,
      bankReference: payForm.bank_reference || null, invoiceAllocations: allocations,
      notes: payForm.notes || null
    };
    try {
      await apiFetch('/api/revenue/payments', { method: 'POST', body: JSON.stringify(body) });
      resetDirty(); setPayModalOpen(false); fetchData();
    } catch (e) { /* handled */ }
  }

  async function fetchActions(recId) {
    try {
      const res = await apiFetch(`/api/revenue/receivables/${recId}/actions`);
      if (res.ok) { const d = await res.json(); setActions(d.actions || []); }
    } catch (e) { /* handled */ }
    setActionRecId(recId);
    resetDirty();
    setActionModal(true);
  }

  async function saveAction() {
    const body = {
      actionType: actionForm.action_type || null, notes: actionForm.notes || null,
      actionDate: actionForm.action_date || null
    };
    try {
      await apiFetch(`/api/revenue/receivables/${actionRecId}/actions`, { method: 'POST', body: JSON.stringify(body) });
      resetDirty(); fetchActions(actionRecId); fetchData();
      setActionForm({ action_type: '', notes: '', action_date: '' });
    } catch (e) { /* handled */ }
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>;

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ textAlign: 'center', padding: 16, background: '#F0FDF4' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#16A34A' }}>{fmtCurrency(totalRecognized)}</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Recognized Revenue</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16, background: '#FEF3C7' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#D97706' }}>{fmtCurrency(totalDeferred)}</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Deferred Revenue</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16, background: '#EFF6FF' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#2563EB' }}>{fmtCurrency(totalUnbilled)}</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Unbilled Revenue</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 16, background: '#FEF2F2' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#DC2626' }}>{fmtCurrency(totalReceivable)}</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Total Receivables</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button className={`tab-btn${tab === 'revenue' ? ' active' : ''}`} onClick={() => setTab('revenue')}>Revenue Recognition</button>
        <button className={`tab-btn${tab === 'receivables' ? ' active' : ''}`} onClick={() => setTab('receivables')}>Receivables & Aging</button>
        <button className={`tab-btn${tab === 'payments' ? ' active' : ''}`} onClick={() => setTab('payments')}>Payment Receipts</button>
      </div>

      {/* Revenue Recognition Tab */}
      {tab === 'revenue' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => { setRevForm({ contract_id: '', period: '', recognized: '', deferred: '', unbilled: '', method: '' }); resetDirty(); setRevModalOpen(true); }}>
              + Add Revenue Record
            </button>
          </div>
          <div className="card">
            <table className="data-table">
              <thead><tr><th>Contract</th><th>Period</th><th>Recognized</th><th>Deferred</th><th>Unbilled</th><th>Cumulative</th><th>Method</th></tr></thead>
              <tbody>
                {revRecords.map(rr => (
                  <tr key={rr.id}>
                    <td>{rr.contract_number || rr.contract_id || '--'}</td>
                    <td>{rr.period || '--'}</td>
                    <td>{fmtCurrency(rr.recognized)}</td>
                    <td>{fmtCurrency(rr.deferred)}</td>
                    <td>{fmtCurrency(rr.unbilled)}</td>
                    <td>{fmtCurrency(rr.cumulative)}</td>
                    <td>{rr.method || '--'}</td>
                  </tr>
                ))}
                {revRecords.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No revenue records</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Receivables Tab */}
      {tab === 'receivables' && (
        <>
          {/* Aging Report */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3>Aging Report</h3>
            <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
              {AGING_BUCKETS.map(bucket => {
                const bucketData = aging.find(a => a.aging_bucket === bucket);
                const amt = bucketData ? parseFloat(bucketData.total_amount || 0) : 0;
                const count = bucketData ? parseInt(bucketData.count || 0) : 0;
                const maxAmt = Math.max(...aging.map(a => parseFloat(a.total_amount || 0)), 1);
                const barPct = (amt / maxAmt) * 100;

                return (
                  <div key={bucket} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{bucket} days</div>
                    <div style={{ height: 80, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: 4 }}>
                      <div style={{ width: '60%', height: `${Math.max(barPct, 5)}%`, background: AGING_COLORS[bucket], borderRadius: '4px 4px 0 0', transition: 'height 0.3s' }} />
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{fmtCurrency(amt)}</div>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{count} invoice{count !== 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Receivables List */}
          <div className="card">
            <h3>Receivables</h3>
            <table className="data-table">
              <thead><tr><th>Invoice</th><th>Customer</th><th>Amount</th><th>Due Date</th><th>Aging</th><th>Status</th><th>Last Follow-up</th><th>Actions</th></tr></thead>
              <tbody>
                {receivables.map(r => (
                  <tr key={r.id}>
                    <td>{r.invoice_number || '--'}</td>
                    <td>{r.customer_name || '--'}</td>
                    <td>{fmtCurrency(r.amount)}</td>
                    <td>{fmtDate(r.due_date)}</td>
                    <td><span style={{ color: AGING_COLORS[r.aging_bucket] || '#64748B', fontWeight: 600 }}>{r.aging_bucket || '--'}</span></td>
                    <td><span className={`badge ${STATUS_BADGES[r.collection_status] || 'badge-gray'}`}>{r.collection_status}</span></td>
                    <td>{fmtDate(r.last_followup)}</td>
                    <td>
                      <button className="btn btn-secondary btn-xs" onClick={() => fetchActions(r.id)}>Actions</button>
                    </td>
                  </tr>
                ))}
                {receivables.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No receivables</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Payments Tab */}
      {tab === 'payments' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => { setPayForm({ customer_id: '', amount: '', payment_date: '', payment_method: '', bank_reference: '', invoice_id: '', notes: '' }); resetDirty(); setPayModalOpen(true); }}>
              + Record Payment
            </button>
          </div>
          <div className="card">
            <table className="data-table">
              <thead><tr><th>Customer</th><th>Amount</th><th>Date</th><th>Method</th><th>Bank Ref</th><th>Notes</th></tr></thead>
              <tbody>
                {payments.map(pm => (
                  <tr key={pm.id}>
                    <td>{pm.customer_name || '--'}</td>
                    <td><strong>{fmtCurrency(pm.amount)}</strong></td>
                    <td>{fmtDate(pm.payment_date)}</td>
                    <td>{pm.payment_method || '--'}</td>
                    <td>{pm.bank_reference || '--'}</td>
                    <td>{pm.notes || '--'}</td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No payments recorded</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Revenue Modal */}
      {revModalOpen && (
        <Modal title="Add Revenue Record" onClose={() => setRevModalOpen(false)} dirty={dirty}>
          <FormField label="Contract" type="select" options={contracts.map(c => ({ value: c.id, label: c.contract_number }))} value={revForm.contract_id} onChange={v => { setRevForm(f => ({ ...f, contract_id: v })); markDirty(); }} />
          <FormField label="Period (e.g. 2026-Q1)" value={revForm.period} onChange={v => { setRevForm(f => ({ ...f, period: v })); markDirty(); }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <FormField label="Recognized" type="number" value={revForm.recognized} onChange={v => { setRevForm(f => ({ ...f, recognized: v })); markDirty(); }} />
            <FormField label="Deferred" type="number" value={revForm.deferred} onChange={v => { setRevForm(f => ({ ...f, deferred: v })); markDirty(); }} />
            <FormField label="Unbilled" type="number" value={revForm.unbilled} onChange={v => { setRevForm(f => ({ ...f, unbilled: v })); markDirty(); }} />
          </div>
          <FormField label="Method" type="select" options={REV_METHODS.map(m => ({ value: m, label: m }))} value={revForm.method} onChange={v => { setRevForm(f => ({ ...f, method: v })); markDirty(); }} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setRevModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveRevenue}>Save</button>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {payModalOpen && (
        <Modal title="Record Payment" onClose={() => setPayModalOpen(false)} dirty={dirty}>
          <FormField label="Customer" type="select" options={customers.map(c => ({ value: c.id, label: c.name }))} value={payForm.customer_id} onChange={v => { setPayForm(f => ({ ...f, customer_id: v })); markDirty(); }} />
          <FormField label="Invoice (optional)" type="select" options={[{ value: '', label: '-- Select --' }, ...invoices.map(i => ({ value: i.id, label: i.invoice_number }))]} value={payForm.invoice_id} onChange={v => { setPayForm(f => ({ ...f, invoice_id: v })); markDirty(); }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Amount" type="number" value={payForm.amount} onChange={v => { setPayForm(f => ({ ...f, amount: v })); markDirty(); }} required />
            <FormField label="Payment Date" type="date" value={payForm.payment_date} onChange={v => { setPayForm(f => ({ ...f, payment_date: v })); markDirty(); }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Payment Method" type="select" options={PAYMENT_METHODS.map(m => ({ value: m, label: m }))} value={payForm.payment_method} onChange={v => { setPayForm(f => ({ ...f, payment_method: v })); markDirty(); }} />
            <FormField label="Bank Reference" value={payForm.bank_reference} onChange={v => { setPayForm(f => ({ ...f, bank_reference: v })); markDirty(); }} />
          </div>
          <FormField label="Notes" type="textarea" value={payForm.notes} onChange={v => { setPayForm(f => ({ ...f, notes: v })); markDirty(); }} />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setPayModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={savePayment}>Record Payment</button>
          </div>
        </Modal>
      )}

      {/* Collection Actions Modal */}
      {actionModal && (
        <Modal title="Collection Actions" onClose={() => setActionModal(false)} wide dirty={dirty}>
          <div style={{ marginBottom: 16 }}>
            <h4>Log New Action</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Action Type" type="select" options={[
                { value: 'phone_call', label: 'Phone Call' },
                { value: 'email', label: 'Email' },
                { value: 'letter', label: 'Letter' },
                { value: 'meeting', label: 'Meeting' },
                { value: 'escalation', label: 'Escalation' }
              ]} value={actionForm.action_type} onChange={v => { setActionForm(f => ({ ...f, action_type: v })); markDirty(); }} />
              <FormField label="Date" type="date" value={actionForm.action_date} onChange={v => { setActionForm(f => ({ ...f, action_date: v })); markDirty(); }} />
            </div>
            <FormField label="Notes" type="textarea" value={actionForm.notes} onChange={v => { setActionForm(f => ({ ...f, notes: v })); markDirty(); }} />
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={saveAction}>Add Action</button>
          </div>

          <h4>Action History</h4>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Type</th><th>By</th><th>Notes</th></tr></thead>
            <tbody>
              {actions.map(a => (
                <tr key={a.id}>
                  <td>{fmtDate(a.action_date)}</td>
                  <td>{a.action_type || '--'}</td>
                  <td>{a.action_by_name || '--'}</td>
                  <td>{a.notes || '--'}</td>
                </tr>
              ))}
              {actions.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94A3B8' }}>No actions recorded</td></tr>}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}
